import React from "react";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Divider,
  InputNumber,
  Button,
  Table,
} from "antd";
import { ShoppingCartOutlined } from "@ant-design/icons";
import {
  Supplier,
  Product,
  PurchaseItem,
  PurchasePaymentStatus,
} from "../../../Types";

const { Option } = Select;

interface EditablePurchaseItem extends PurchaseItem {
  stocks?: any[];
}

interface PurchaseFormProps {
  form: any;
  suppliers: Supplier[];
  setSupplierModalOpen: (open: boolean) => void;
  productSearchText: string;
  setProductSearchText: (val: string) => void;
  handleProductSelect: (value: string | number) => void;
  allProducts: Product[];
  purchaseItems: EditablePurchaseItem[];
  formTableColumns: any[];
  globalDiscount: number;
  setGlobalDiscount: (val: number) => void;
  grossTotal: number;
  paidAmount: number;
  setPaidAmount: (val: number) => void;
  showDueDate: boolean;
  grandTotal: number;
  dueAmount: number;
}

export const PurchaseForm: React.FC<PurchaseFormProps> = ({
  form,
  suppliers,
  setSupplierModalOpen,
  productSearchText,
  setProductSearchText,
  handleProductSelect,
  allProducts,
  purchaseItems,
  formTableColumns,
  globalDiscount,
  setGlobalDiscount,
  grossTotal,
  paidAmount,
  setPaidAmount,
  showDueDate,
  grandTotal,
  dueAmount,
}) => {
  return (
    <Form form={form} layout="vertical">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Form.Item
          label={
            <div className="flex items-center justify-between w-full gap-x-1">
              <span>Supplier</span>
              <Button
                type="link"
                size="small"
                className="p-0 h-auto text-[11px]"
                onClick={() => setSupplierModalOpen(true)}
              >
                (Add New)
              </Button>
            </div>
          }
          name="supplier_id"
          rules={[{ required: true, message: "Please select a supplier." }]}
        >
          <Select
            placeholder="Select supplier"
            showSearch
            optionFilterProp="children"
          >
            {suppliers?.map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Purchased Date"
          name="purchase_date"
          rules={[{ required: true, message: "Date is required." }]}
        >
          <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
        </Form.Item>

        <Form.Item label="Invoice Number" name="invoice_no">
          <Input placeholder="Supplier Invoice No." />
        </Form.Item>

        <Form.Item label="PO Number" name="po_no">
          <Input placeholder="Purchase Order Ref No." />
        </Form.Item>
      </div>

      <Divider orientation="left">
        <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">
          Purchase Items List
        </span>
      </Divider>

      <div className="mb-4 bg-gray-50/50 p-4 rounded-xl border border-gray-200/60 flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          <Select
            showSearch
            placeholder="Search Product to Add (by Title or Barcode)..."
            filterOption={false}
            onSearch={(val) => setProductSearchText(val)}
            searchValue={productSearchText}
            onChange={handleProductSelect}
            value={null}
            style={{ width: "100%" }}
          >
            {allProducts
              .filter((p) => {
                if (!productSearchText.trim()) return true;
                const searchLower = productSearchText.toLowerCase();
                return (
                  p.title.toLowerCase().includes(searchLower) ||
                  (p.barcode &&
                    p.barcode.toLowerCase().includes(searchLower)) ||
                  (p.sku_code && p.sku_code.toLowerCase().includes(searchLower))
                );
              })
              .map((p) => (
                <Option key={p.id} value={p.id}>
                  {p.title} ({p.barcode || p.sku_code})
                </Option>
              ))}
            {productSearchText.trim() &&
              !allProducts.some(
                (p) =>
                  p.title.toLowerCase() ===
                  productSearchText.trim().toLowerCase(),
              ) && (
                <Option
                  key="ADD_NEW_PRODUCT"
                  value="ADD_NEW_PRODUCT"
                  className="text-indigo-600 font-medium"
                >
                  + Add "{productSearchText.trim()}" as a new product
                </Option>
              )}
          </Select>
        </div>
        <span className="text-xs text-gray-400 font-medium">
          Add products to build the purchase sheet.
        </span>
      </div>

      {purchaseItems.length === 0 ? (
        <div className="py-12 border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center bg-gray-50/20">
          <ShoppingCartOutlined
            style={{ fontSize: "28px" }}
            className="text-gray-300 mb-2"
          />
          <span className="text-xs text-gray-400 font-medium">
            No items added to the purchase order yet.
          </span>
        </div>
      ) : (
        <Table
          dataSource={purchaseItems}
          columns={formTableColumns}
          pagination={false}
          size="small"
          rowKey={(rec, index) =>
            rec.product_id ? `${rec.product_id}-${index}` : `new-${index}`
          }
          bordered
          className="mb-6 overflow-hidden rounded-xl border border-gray-200"
          scroll={{ x: 900 }}
        />
      )}

      <Divider orientation="left">
        <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">
          Checkout Summary
        </span>
      </Divider>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4 bg-gray-50/30 p-6 rounded-xl border border-gray-200/80 shadow-inner">
        <div className="flex flex-col gap-4">
          <Form.Item
            label="Global Discount Amount"
            style={{ marginBottom: "8px" }}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              max={grossTotal}
              value={globalDiscount}
              onChange={(v) => setGlobalDiscount(v || 0)}
              formatter={(v) => `Rs. ${v}`}
              parser={(v) => (v ? v.replace(/Rs\.\s?|(,*)/g, "") : "") as any}
            />
          </Form.Item>

          <Form.Item label="Paid Amount" style={{ marginBottom: "8px" }}>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              value={paidAmount}
              onChange={(v) => setPaidAmount(v || 0)}
              formatter={(v) => `Rs. ${v}`}
              parser={(v) => (v ? v.replace(/Rs\.\s?|(,*)/g, "") : "") as any}
            />
          </Form.Item>
        </div>

        <div className="flex flex-col gap-4">
          <Form.Item
            label="Payment Status"
            name="payment_status"
            style={{ marginBottom: "8px" }}
          >
            <Select disabled>
              <Option value={PurchasePaymentStatus.PAID}>Paid</Option>
              <Option value={PurchasePaymentStatus.PARTIAL}>Partial</Option>
              <Option value={PurchasePaymentStatus.DUE}>Due</Option>
            </Select>
          </Form.Item>

          {showDueDate && (
            <Form.Item
              label="Payment Due Date"
              name="due_date"
              rules={[
                {
                  required: showDueDate,
                  message: "Due Date is required for partial/unpaid purchases.",
                },
              ]}
              style={{ marginBottom: "8px" }}
            >
              <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
            </Form.Item>
          )}
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 flex flex-col justify-center gap-3">
          <div className="flex items-center justify-between text-xs text-gray-500 font-medium">
            <span>Gross Purchase Total:</span>
            <span>Rs. {grossTotal.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 font-medium border-b border-gray-100 pb-2">
            <span>Global Discount:</span>
            <span className="text-red-400">
              -Rs. {globalDiscount.toFixed(2)}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-700 font-bold">
            <span>Grand Total:</span>
            <span className="text-sm">Rs. {grandTotal.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-green-600 font-medium border-b border-gray-100 pb-2">
            <span>Paid Amount:</span>
            <span>Rs. {paidAmount.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-bold text-gray-900 text-xs uppercase tracking-wide">
              Remaining Due:
            </span>
            <span
              className={`text-base font-black ${dueAmount > 0 ? "text-red-500" : "text-green-600"}`}
            >
              Rs. {dueAmount.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </Form>
  );
};
